// frontend/app/discoveryJourneys.js
//
// API module for the Discovery Journey domain — the story layer above Learning
// Loops (design decisions L7/L8: a Journey is an ordered set of lesson plans
// plus mission story and per-loop bridge prompts, and a Loop *is* a lesson
// plan).
//
// Shaped exactly like lessonPlans.js: a private `handle()` that turns a
// non-2xx into an Error carrying `status` and `detail`, and one thin exported
// `apiX()` per route. Every call goes through the caller's `fetchWithAuth` from
// useAuth() — that is the only path with 401 refresh-and-retry, and every
// discovery-journey route requires a bearer token.
//
// Deliberately NOT the camp `journeys` module: that catalogue is a literal
// array in the bundle. Nothing here knows the name of a single journey.

import { FASTAPI_BASE_URL } from "./config";

const API_JOURNEYS = `${FASTAPI_BASE_URL}/discovery-journeys`;

async function handle(res) {
  let data = null;
  let text = null;
  try {
    data = await res.json();
  } catch {
    try {
      text = await res.text();
    } catch {
      text = null;
    }
  }

  if (!res.ok) {
    const errMsg =
      data?.detail?.message ||
      (typeof data?.detail === "string" ? data.detail : null) ||
      data?.message ||
      data?.error ||
      text ||
      `Request failed (${res.status}).`;
    const error = new Error(errMsg);
    error.status = res.status;
    error.detail = data?.detail ?? null;
    throw error;
  }

  return data;
}

const jsonPost = (body) => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

const studentBase = (studentEmail, journeyId) =>
  `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/discovery-journeys/${journeyId}`;

// ---------- reading ----------

/**
 * Journeys for a board, newest curriculum mapping first. `includeDrafts` is
 * honoured server-side only for teacher-like callers; a student asking for
 * drafts still gets published rows.
 *
 * With `studentEmail`, each row carries
 * `progress: { loop_index, completed_loops, loop_count, completed, started }`
 * — the whole page in one call rather than a state lookup per card. Without it
 * `progress` is `null`, which the launcher reads as "not started" rather than
 * as zero progress.
 */
export async function apiListDiscoveryJourneys(
  fetchWithAuth,
  { board, chapterId, includeDrafts = false, studentEmail } = {}
) {
  const url = `${API_JOURNEYS}${query({
    board,
    chapter_id: chapterId,
    include_drafts: includeDrafts ? "true" : null,
    student_email: studentEmail,
  })}`;
  return handle(await fetchWithAuth(url));
}

/**
 * The whole journey resolved for rendering: mission, objectives, success
 * criteria, and the ordered loops with their lesson metadata, bridge prompts
 * and this student's progress.
 *
 * The payload is FLAT — `title`, `mission_story`, `objectives`,
 * `success_criteria` and `loops` all sit at the top level beside each other,
 * mirroring `services/lesson_runtime_builder.build_runtime_payload`, which is
 * the runtime this one sits next to. So the response goes straight in:
 * `<DiscoveryJourneyMap journey={runtime} />`.
 *
 * No `language_id` / `mode`: the journey layer carries no localizable content
 * and no teacher-only prose. Those belong on
 * `GET /api/lesson-plans/{id}/runtime`, which is hit when a student opens a
 * Loop.
 *
 * Without `studentEmail` there is no learner to report on, so every available
 * loop comes back `not_started` — that is the authoring and teach-mode view,
 * not a claim that nobody has started. `studentEmail` is authorized
 * server-side: a student naming another student is a 403, not empty progress.
 *
 * A member lesson that cannot be resolved comes back as a loop with
 * `{status: "unavailable", reason}` rather than being dropped — the map renders
 * it as unavailable and the rest of the chain still runs (design § Error
 * handling). `lesson_plan_id` is NOT NULL server-side, so `status` is the field
 * that says a loop cannot be opened.
 */
export async function apiGetDiscoveryJourneyRuntime(
  fetchWithAuth,
  journeyId,
  { studentEmail } = {}
) {
  const url = `${API_JOURNEYS}/${journeyId}/runtime${query({
    student_email: studentEmail,
  })}`;
  return handle(await fetchWithAuth(url));
}

/**
 * Cross-loop mastery: every concept declared by any lesson in the journey with
 * either this student's band (`student_email`) or the class mean plus its
 * four-band distribution (`class_id`).
 *
 * A concept with no evidence comes back with `mastery: null`, never 0.0 — see
 * the design's error-handling note. The renderer must keep that distinction.
 */
export async function apiGetJourneyMasteryMap(
  fetchWithAuth,
  journeyId,
  { classId, studentEmail } = {}
) {
  const url = `${API_JOURNEYS}/${journeyId}/mastery-map${query({
    class_id: classId,
    student_email: studentEmail,
  })}`;
  return handle(await fetchWithAuth(url));
}

export async function apiGetDiscoveryJourneyState(
  fetchWithAuth,
  studentEmail,
  journeyId
) {
  return handle(await fetchWithAuth(`${studentBase(studentEmail, journeyId)}/state`));
}

export async function apiSaveDiscoveryJourneyState(
  fetchWithAuth,
  studentEmail,
  journeyId,
  {
    stateJson = {},
    lastActiveLoopId = null,
    loopIndex = 0,
    completed = false,
  } = {}
) {
  return handle(
    await fetchWithAuth(`${studentBase(studentEmail, journeyId)}/state`, {
      ...jsonPost({
        state_json: stateJson,
        last_active_loop_id: lastActiveLoopId,
        loop_index: loopIndex,
        completed,
      }),
      method: "PUT",
    })
  );
}

// ---------- authoring ----------
// Every mutating route requires a bearer token and a teacher-like role; the
// server is the authority, these just carry the call.

/** Create a journey. `slug` is normalized server-side; a duplicate returns 409. */
export async function apiCreateDiscoveryJourney(fetchWithAuth, journey) {
  return handle(await fetchWithAuth(API_JOURNEYS, jsonPost(journey)));
}

/**
 * Update journey-level fields. Passing `loops` REPLACES the whole ordered set —
 * use apiSaveDiscoveryJourneyLoops for that so the intent is legible at the
 * call site.
 */
export async function apiPatchDiscoveryJourney(fetchWithAuth, journeyId, patch) {
  return handle(
    await fetchWithAuth(`${API_JOURNEYS}/${journeyId}`, {
      ...jsonPost(patch),
      method: "PATCH",
    })
  );
}

export async function apiDeleteDiscoveryJourney(fetchWithAuth, journeyId) {
  const res = await fetchWithAuth(`${API_JOURNEYS}/${journeyId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) return handle(res);
  return { deleted: true };
}

/**
 * Replace the journey's ordered loops in one call. `loops` is the FULL set —
 * `sequence` is re-assigned 1..N from array order server-side, so reordering is
 * the same call as editing.
 *
 * Each entry: `{ lesson_plan_id, loop_title, loop_subtitle, bridge_prompt }`.
 */
export async function apiSaveDiscoveryJourneyLoops(fetchWithAuth, journeyId, loops) {
  return handle(
    await fetchWithAuth(`${API_JOURNEYS}/${journeyId}/loops`, {
      ...jsonPost({ loops: Array.isArray(loops) ? loops : [] }),
      method: "PUT",
    })
  );
}
